import React, { useMemo, useState } from 'react';
import type { Invoice } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { usePaymentCheckoutStore, useInvoiceInfo, useInvoiceDrawerStore } from '@hero/hooks-shared';
import { useOrderExportApi, useInvoiceApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { useNav } from '@hero/web-hooks';
import { PayIcon, DownloadIcon, UploadIcon, CashIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { InvoiceSummaryTab } from './InvoiceSummaryTab';
import { InvoiceOrdersTab } from './InvoiceOrdersTab';
import { InvoiceTransactionsTab } from './InvoiceTransactionsTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { UploadInvoiceProofDrawer } from '../UploadProof/UploadInvoiceProofDrawer';
import { PayInvoiceDrawer } from '../Pay/PayInvoiceDrawer';

/**
 * Bilateral invoice viewer role (`@hero/core` `getInvoiceRoleForViewer`).
 * - `buyer`  — the viewer is the party that owes on this invoice (pays).
 * - `seller` — the viewer is the party that issued / is owed (uploads proof).
 * - `auto`   — derive from the viewer's org id at runtime via
 *   `useInvoiceInfo(invoice, viewerOrgId)`.
 */
export type InvoiceDrawerAsRole = 'buyer' | 'seller' | 'auto';

export interface InvoiceDrawerProps {
  /**
   * Viewer's role on the invoice. Drives which action buttons show
   * (Pay vs Upload Proof) and which CTAs appear inside the summary
   * tab. Use `'auto'` when the consumer can pass `viewerOrgId` to
   * derive the role per-invoice (preferred for the upcoming
   * `AccountPage`).
   */
  asRole?: InvoiceDrawerAsRole;
  /**
   * Viewer org id — required when `asRole === 'auto'` so the drawer
   * can resolve role per invoice via `getInvoiceRoleForViewer`.
   */
  viewerOrgId?: string;
  /**
   * @deprecated bilateral SaaS model — use {@link asRole}.
   * `'customer'` → `'buyer'`, `'carrier'` → `'seller'`. Kept so the
   * shared `WalletPage` (parked, slated to be replaced by
   * `AccountPage`) keeps rendering until cutover.
   */
  displayForUser?: 'customer' | 'carrier';
  syncUrl?: boolean | string;
}

interface InvoiceDrawerContentProps {
  invoice: Invoice;
  asRole: 'buyer' | 'seller';
  /**
   * Viewer org id — threaded through so `InvoiceSummaryTab` can
   * run its seller-side financing eligibility check inline.
   */
  viewerOrgId?: string;
  onClose: () => void;
}

const resolveAsRole = (
  props: Pick<InvoiceDrawerProps, 'asRole' | 'displayForUser' | 'viewerOrgId'>,
  invoice?: Invoice,
): 'buyer' | 'seller' => {
  if (props.asRole === 'buyer' || props.asRole === 'seller') return props.asRole;
  if (props.displayForUser === 'customer') return 'buyer';
  if (props.displayForUser === 'carrier') return 'seller';
  if (props.asRole === 'auto' && invoice && props.viewerOrgId) {
    const buyerId = invoice.buyer?._id?.toString?.() ?? String(invoice.buyer?._id ?? '');
    if (buyerId && buyerId === props.viewerOrgId) return 'buyer';
  }
  return 'seller';
};

const InvoiceDrawerContent: React.FC<InvoiceDrawerContentProps> = ({
  invoice,
  asRole,
  viewerOrgId,
  onClose,
}) => {
  const { t } = useTranslation(['m-payment', 'm-invoice']);
  const { navigate } = useNav();
  const openPayment = usePaymentCheckoutStore(s => s.open);
  const subCreateInvoice = useFeature('invoice_module', 'sub_create_invoice');
  const subEditInvoice = useFeature('invoice_module', 'sub_edit_invoice');
  const { download: exportOrders, isPending: isExporting } = useOrderExportApi({
    filename: `invoice-orders-${invoice.ref.main}.xlsx`,
  });
  const invoiceInfo = useInvoiceInfo(invoice, viewerOrgId);
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('summary');

  const tabs: { id: string; label: string }[] = [
    {
      id: 'summary',
      label: t('m-invoice:tabs.summary', {
        defaultValue: t('m-payment:invoice.drawer.tabs.summary'),
      }),
    },
    {
      id: 'orders',
      label: t('m-invoice:tabs.orders', {
        defaultValue: t('m-payment:invoice.drawer.tabs.orders'),
      }),
    },
    {
      id: 'transactions',
      label: t('m-invoice:tabs.transactions', {
        defaultValue: t('m-payment:invoice.drawer.tabs.transactions'),
      }),
    },
  ];

  const actions: ActionButtonGroup[] = useMemo(() => {
    const items: ActionButtonGroup[] = [];

    if (asRole === 'buyer' && subCreateInvoice.isEnabled) {
      items.push({
        id: 'pay',
        label: t('m-payment:actions.pay_now'),
        description: t('m-payment:invoice.drawer.pay_now_description'),
        icon: <PayIcon className="w-4 h-4" />,
        onClick: () => {
          onClose();
          openPayment({ mode: 'invoice', invoices: [invoice] });
        },
        disabled: !invoiceInfo?.canPay,
        highlight: true,
      });
    }

    // Role-neutral "Record payment" — either party on the invoice
    // can close it via an off-platform payment (`payInvoiceAPI`).
    // Shown when there's still something to pay; gated on
    // `subEditInvoice` (the same feature flag that lets the seller
    // edit the invoice) so back-office tenants can switch it off.
    const hasRemainingAmount = (invoice.amount?.remaining ?? 0) > 0;
    if (
      !invoiceInfo?.isPaid &&
      hasRemainingAmount &&
      subEditInvoice.isEnabled
    ) {
      items.push({
        id: 'record_payment',
        label: t('m-payment:invoice.actions.record_payment', {
          defaultValue: 'Record payment',
        }),
        description: t('m-payment:invoice.drawer.record_payment_description', {
          defaultValue:
            'Mark an off-platform payment against this invoice — the transaction posts to the bilateral account.',
        }),
        icon: <CashIcon className="w-4 h-4" />,
        onClick: () => setRecordPaymentOpen(true),
        highlight: asRole === 'seller',
      });
    }

    if (asRole === 'seller' && invoiceInfo?.canUploadInvoice && subEditInvoice.isEnabled) {
      items.push({
        id: 'upload_proof',
        label: t('m-payment:invoice.drawer.upload_proof'),
        description: t('m-payment:invoice.drawer.upload_proof_description'),
        icon: <UploadIcon className="w-4 h-4" />,
        onClick: () => setUploadDrawerOpen(true),
      });
    }

    items.push({
      id: 'export',
      label: t('m-payment:invoice.drawer.export_orders'),
      description: t('m-payment:invoice.drawer.export_orders_description'),
      icon: <DownloadIcon className="w-4 h-4" />,
      onClick: () => exportOrders({ invoiceId: getObjectId(invoice._id) }),
      isLoading: isExporting,
    });

    return items;
  }, [
    invoice,
    invoiceInfo?.canPay,
    invoiceInfo?.canUploadInvoice,
    invoiceInfo?.isPaid,
    asRole,
    subCreateInvoice.isEnabled,
    subEditInvoice.isEnabled,
    t,
    onClose,
    openPayment,
    exportOrders,
    isExporting,
  ]);

  const handleApplyFinancing = (inv: Invoice) => {
    // Land on the "New application" tab with the invoice pre-
    // selected. Without `tab=apply`, the financing page defaults
    // to `overview` and the user has to manually click into
    // Apply to see the seeded form. `invoiceId` is consumed by
    // `ApplyForm`'s deep-link effect and stripped from the URL
    // after the form picks it up.
    navigate('financing', { invoiceId: String(inv._id), tab: 'apply' });
    onClose();
  };

  const handleViewFinancingApplication = (applicationId: string) => {
    navigate('financing', { tab: 'history', applicationId });
    onClose();
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return (
          <InvoiceSummaryTab
            invoice={invoice}
            asRole={asRole}
            viewerOrgId={viewerOrgId}
            onUploadProof={() => setUploadDrawerOpen(true)}
            onApplyFinancing={handleApplyFinancing}
            onViewFinancingApplication={handleViewFinancingApplication}
          />
        );
      case 'orders':
        return <InvoiceOrdersTab invoiceId={invoice._id} asRole={asRole} />;
      case 'transactions':
        return <InvoiceTransactionsTab invoiceId={invoice._id} asRole={asRole} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={invoice.ref?.main || invoice.ref?.short || ''}
        subtitle={
          asRole === 'buyer' ? invoice.seller?.name : invoice.buyer?.name
        }
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
      {actions.length > 0 && (
        <DrawerFooter actions={actions} buttonLabel={t('m-payment:invoice.drawer.actions')} />
      )}
      {asRole === 'seller' && subEditInvoice.isEnabled && (
        <UploadInvoiceProofDrawer
          invoice={invoice}
          open={uploadDrawerOpen}
          onOpenChange={setUploadDrawerOpen}
        />
      )}
      <PayInvoiceDrawer
        invoice={invoice}
        open={recordPaymentOpen}
        onOpenChange={setRecordPaymentOpen}
      />
    </>
  );
};

/**
 * Self-contained Invoice Drawer.
 *
 * Render ONE instance per page. Open from anywhere via:
 * ```ts
 * useInvoiceDrawerStore.getState().open(invoiceId)
 * ```
 */
export const InvoiceDrawer: React.FC<InvoiceDrawerProps> = props => (
  <StackedDrawer<Invoice>
    useStore={useInvoiceDrawerStore}
    useItemApi={id => useInvoiceApi(id)}
    syncUrl={props.syncUrl}
    defaultParamKey="invoiceId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Invoice Details"
    description="View invoice information"
    renderContent={(invoice, onClose) => (
      <InvoiceDrawerContent
        invoice={invoice}
        asRole={resolveAsRole(props, invoice)}
        viewerOrgId={props.viewerOrgId}
        onClose={onClose}
      />
    )}
  />
);
